import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Hammer, LogOut, User } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const linkClass = ({ isActive }) =>
  `text-sm font-medium transition-colors hover:text-slate-900 ${
    isActive ? "text-slate-900" : "text-slate-600"
  }`;

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-md bg-blue-900 text-white grid place-items-center">
            <Hammer className="w-5 h-5" />
          </span>
          <span className="font-display font-bold text-xl text-slate-900 tracking-tight">
            Fixus<span className="text-orange-600">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/jobs" data-testid="nav-jobs" className={linkClass}>
            Browse Jobs
          </NavLink>
          <NavLink to="/tradespeople" data-testid="nav-tradespeople" className={linkClass}>
            Find a Pro
          </NavLink>
          <NavLink to="/post-job" data-testid="nav-post-job" className={linkClass}>
            Post a Job
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Button
                variant="ghost"
                onClick={() => nav("/login")}
                data-testid="header-signin"
                className="text-slate-700"
              >
                Sign In
              </Button>
              <Button
                onClick={() => nav("/register")}
                data-testid="header-signup"
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                Get Started
              </Button>
            </>
          ) : (
            <>
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="header-user-menu"
                  className="border-slate-300"
                >
                  <User className="w-4 h-4 mr-2" /> {user.name.split(" ")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">{user.role}</div>
                  <div className="font-medium text-slate-900 truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")}>
                  Dashboard
                </DropdownMenuItem>
                {user.role === "homeowner" && (
                  <DropdownMenuItem data-testid="menu-post-job" onClick={() => nav("/post-job")}>
                    Post a Job
                  </DropdownMenuItem>
                )}
                {user.role === "tradesperson" && (
                  <DropdownMenuItem data-testid="menu-profile-edit" onClick={() => nav("/profile/edit")}>
                    Edit Profile
                  </DropdownMenuItem>
                )}
                {user.role === "homeowner" && (
                  <DropdownMenuItem data-testid="menu-account-settings" onClick={() => nav("/profile/edit")}>
                    Account settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={async () => {
                    await logout();
                    nav("/");
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
